import { Copy, MessageCircle } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { Button, toast } from '@hrms/ui';
import { copyToClipboard } from '../../lib/clipboard.js';
import { useAuthenticatedSession } from '../../session/SessionProvider.js';
import { APP_DOMAIN, isDevOffAppDomain, orgSubdomainOrigin } from '../../lib/subdomain.js';
import { courseSlug } from './courseSlug.js';

/** The fields a share needs — satisfied by both `TrainingTaskSummary` and the full task DTO. */
export interface ShareableCourse {
  id: string;
  title: string;
  /** The course's short link code; without one the link falls back to the course id. */
  shareCode?: string | null;
}

/**
 * Where a shared link points: the company's own address, so a colleague who opens it lands in the
 * right workspace. Never the page's own origin when that is not a real address someone else can
 * open — the packaged phone app runs from an internal origin (`https://localhost`,
 * `capacitor://localhost`). Local development keeps the dev server's origin, which is what
 * actually routes back to this machine.
 */
export function courseShareOrigin(orgSlug: string | null | undefined): string {
  if (Capacitor.isNativePlatform()) {
    return orgSlug ? `https://${orgSlug}.${APP_DOMAIN}` : `https://${APP_DOMAIN}`;
  }
  const host = window.location.hostname;
  if (!orgSlug || host === 'localhost' || host === '127.0.0.1' || isDevOffAppDomain()) {
    return window.location.origin;
  }
  return orgSubdomainOrigin(orgSlug);
}

/**
 * The path part of a shared course link: `/learning/c/<short code>/<name>`. The code belongs to the
 * course for good, so the link keeps working after the course is renamed or edited into a new
 * version; the name is only there to make it readable (it is ignored). Without a code (the instant
 * before a new course gets one) it falls back to the longer id form, which works the same way.
 */
export function courseSharePath(course: ShareableCourse): string {
  // Encoded: a slug keeps non-Latin letters (a Hindi title stays readable once decoded by the browser).
  const name = encodeURIComponent(courseSlug(course.title));
  return course.shareCode
    ? `/learning/c/${course.shareCode}/${name}`
    : `/learning/courses/${course.id}/${name}`;
}

/**
 * The employee-facing link to a course. Signed-in only — a signed-out visitor goes to sign-in (Zoho
 * or email) and comes straight back — and it opens the visitor's OWN assignment of the course, so
 * the link grants nothing by itself.
 */
export function courseShareUrl(course: ShareableCourse, orgSlug?: string | null): string {
  return `${courseShareOrigin(orgSlug)}${courseSharePath(course)}`;
}

export function courseWhatsAppUrl(course: ShareableCourse, orgSlug?: string | null): string {
  const text = `Training: ${course.title}\nOpen the course: ${courseShareUrl(course, orgSlug)}`;
  return `https://wa.me/?text=${encodeURIComponent(text)}`;
}

/** Copy the course link, with the toast every share point uses. */
export async function copyCourseLink(course: ShareableCourse, orgSlug?: string | null): Promise<void> {
  const url = courseShareUrl(course, orgSlug);
  if (await copyToClipboard(url)) {
    toast.success('Course link copied', {
      description: 'People sign in to open it; it plays for anyone the course is assigned to.',
    });
  } else {
    toast.error('Could not copy the link', { description: url });
  }
}

/**
 * Copy link / Share on WhatsApp for one course — same pattern as `LiveSessionShareActions`.
 * `compact` renders icon-only buttons for a table row; the labelled form suits a page header.
 */
export function ShareCourseActions({
  course,
  compact = false,
  disabled = false,
  disabledReason,
}: {
  course: ShareableCourse;
  compact?: boolean;
  disabled?: boolean;
  /** Tooltip shown while `disabled` — e.g. "Publish the course first". */
  disabledReason?: string;
}) {
  const { me } = useAuthenticatedSession();
  const orgSlug = me.person.orgSlug;
  const title = disabled ? disabledReason : undefined;

  return (
    <div className="flex flex-wrap gap-2">
      <Button
        variant={compact ? 'ghost' : 'secondary'}
        size="sm"
        disabled={disabled}
        title={title ?? (compact ? 'Copy course link' : undefined)}
        aria-label={compact ? `Copy link to ${course.title}` : undefined}
        onClick={() => void copyCourseLink(course, orgSlug)}
      >
        <Copy className="size-4" />
        {!compact && 'Copy link'}
      </Button>
      <Button
        variant={compact ? 'ghost' : 'secondary'}
        size="sm"
        disabled={disabled}
        title={title ?? (compact ? 'Share on WhatsApp' : undefined)}
        aria-label={compact ? `Share ${course.title} on WhatsApp` : undefined}
        onClick={() => window.open(courseWhatsAppUrl(course, orgSlug), '_blank', 'noopener,noreferrer')}
      >
        <MessageCircle className="size-4" />
        {!compact && 'Share on WhatsApp'}
      </Button>
    </div>
  );
}
